import { User, Car, Phone, Star } from "lucide-react";
import { useTranslation } from "react-i18next";

/**
 * DriverCard — public snapshot of the assigned driver shown to the customer.
 * Accepts an object like:
 *   { name, phone, picture, vehicle: { plate, color, model }, is_online }
 * Renders nothing if `profile` is falsy.
 *
 * Styled to match the "Tactical Premium" identity (obsidian + amber-gold +
 * electric-teal) used by RideRequest.jsx.
 */
export default function DriverCard({ profile, className = "" }) {
  const { t } = useTranslation();
  if (!profile) return null;
  const veh = profile.vehicle || {};
  const hasVehicle = veh.plate || veh.color || veh.model;
  const hasSpecs = veh.taxi_type || veh.seating_capacity || veh.truck_tonnage_band;
  const rating = profile.rating != null ? Number(profile.rating).toFixed(1) : null;
  return (
    <div className={`bg-obsidian-elev border border-white/10 rounded-2xl p-4 shadow-elev ${className}`} data-testid="driver-card">
      <div className="flex items-center gap-3">
        {profile.picture ? (
          <img src={profile.picture} alt={profile.name} className="w-16 h-16 rounded-2xl object-cover border-2 border-electric-teal shadow-teal" data-testid="driver-photo" />
        ) : (
          <div className="w-16 h-16 rounded-2xl bg-white/[0.06] flex items-center justify-center border border-white/10" data-testid="driver-photo-placeholder">
            <User size={26} className="text-white/50" />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="text-[9px] uppercase font-bold text-electric-teal/80 tracking-[0.22em]">{t("driver.yourDriver", "Your driver")}</div>
          <div className="text-lg font-bold text-white truncate tracking-tight" data-testid="driver-name">{profile.name}</div>
          <div className="flex items-center gap-3 mt-0.5">
            {rating && (
              <div className="inline-flex items-center gap-0.5 text-[11px] font-bold text-amber-gold" data-testid="driver-rating">
                <Star size={11} fill="#FFC800" strokeWidth={0} /> {rating}
              </div>
            )}
            {profile.phone && (
              <a href={`tel:${profile.phone}`} className="text-[11px] text-electric-teal font-semibold inline-flex items-center gap-1" data-testid="driver-phone">
                <Phone size={11} /> {profile.phone}
              </a>
            )}
          </div>
        </div>
      </div>
      {hasVehicle && (
        <div className="mt-4 pt-3 border-t border-white/10 flex items-center gap-3" data-testid="driver-vehicle">
          <div className="w-11 h-11 rounded-xl bg-white/[0.06] border border-white/10 flex items-center justify-center">
            <Car size={18} className="text-electric-teal" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[9px] uppercase font-bold text-white/50 tracking-[0.22em]">{t("driver.vehicle", "Vehicle")}</div>
            <div className="text-sm font-semibold text-white truncate">
              {[veh.color, veh.model].filter(Boolean).join(" · ") || "—"}
            </div>
          </div>
          {veh.plate && (
            <div className="bg-amber-gold text-obsidian rounded-md px-2.5 py-1.5 font-bold text-[13px] tracking-[0.15em] shadow-gold" data-testid="driver-plate">
              {veh.plate.toUpperCase()}
            </div>
          )}
        </div>
      )}
      {hasSpecs && (
        <div className="mt-2 flex flex-wrap gap-1.5" data-testid="driver-specs">
          {veh.taxi_type && (
            <span className="text-[10px] uppercase font-bold bg-electric-teal/10 border border-electric-teal/40 text-electric-teal rounded-full px-2 py-0.5" data-testid="driver-taxi-type">
              {t(`taxi.${veh.taxi_type}`, veh.taxi_type)}
            </span>
          )}
          {veh.seating_capacity && (
            <span className="text-[10px] uppercase font-bold bg-white/[0.06] border border-white/10 text-white rounded-full px-2 py-0.5" data-testid="driver-seats">
              {veh.seating_capacity} {t("taxi.seatsShort", "seats")}
            </span>
          )}
          {veh.truck_tonnage_band && (
            <span className="text-[10px] uppercase font-bold bg-amber-gold/15 border border-amber-gold/40 text-amber-gold rounded-full px-2 py-0.5" data-testid="driver-truck-band">
              {t(`truck.${veh.truck_tonnage_band}`, veh.truck_tonnage_band)}
            </span>
          )}
        </div>
      )}
    </div>
  );
}
